import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  CalendarIcon,
  MobileDatePicker,
  PickersDay,
} from "@mui/x-date-pickers-pro";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useGet } from "../../hooks/api";
import { apiUrls } from "../../utils/urls";
import { Box, Stack } from "@mui/system";
import api from "../../utils/api";
import { showErrorSnackbar, showSuccessSnackbar } from "../../utils/feedback";

export default function SubmitHoursDialog({
  onSubmit,
  date: staticDate,
  hoursSubmissionEndpoint = apiUrls.clock.timeSubmission(),
  clockEventsEndpoint = apiUrls.clock.events(),
  ...disclosureProps
}) {
  const today = dayjs();
  const currentDay = today.date();
  const isStaticDate = staticDate !== undefined;

  let minDate;

  if (currentDay <= 10) {
    const prevMonth = today.subtract(1, "month");
    minDate = prevMonth.startOf("month");
  } else {
    minDate = today.startOf("month");
  }

  // Initialize the picker value to the maxDate in the allowed window
  const [date, setDate] = useState(staticDate ? staticDate : dayjs());
  const [hoursWorked, setHoursWorked] = useState("");
  const [justification, setJustification] = useState("");
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const { data: clockEventData, refetch: refetchClockEventData } = useGet(
    clockEventsEndpoint,
    {
      params: {
        search_params: {
          clock_in_dt__date: date.format("YYYY-MM-DD"),
        },
        values: ["time_worked"],
      },
      staleTime: 0,
    }
  );
  const { data: existingSubmissionData, refetch: refetchSubmissionData } =
    useGet(hoursSubmissionEndpoint, {
      params: {
        search_params: {
          date: date.format("YYYY-MM-DD"),
        },
      },
      staleTime: 0,
    });

  const { data: prevSubmissionData, refetch: refetchPrevSubmissions } = useGet(
    hoursSubmissionEndpoint,
    {
      params: {
        search_params: {
          date__gte: minDate.format("YYYY-MM-DD"),
          date__lt: dayjs().format("YYYY-MM-DD"),
        },
        values: ["date"],
      },
    }
  );

  const prevDays = dayjs().diff(minDate, "days");
  const daysSubmittedSet = useMemo(
    () => new Set((prevSubmissionData?.data || []).map(({ date }) => date)),
    [prevSubmissionData]
  );
  const daysNotSubmitted = prevDays - (daysSubmittedSet.size ?? 0);

  const totalSeconds = useMemo(() => {
    if (!clockEventData) {
      return 0;
    }
    const total = clockEventData.data
      .map(({ time_worked }) => time_worked)
      .reduce((prev, curr) => prev + curr, 0);
    return total;
  }, [clockEventData]);

  const calculatedHoursWorked = (totalSeconds / 3600).toFixed(2);
  const requireReason =
    hoursWorked && parseFloat(hoursWorked) > calculatedHoursWorked;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    const payload = {
      date: date.format("YYYY-MM-DD"),
      hours_worked: parseFloat(hoursWorked),
      justification: justification || null,
    };
    const response = await api.post(hoursSubmissionEndpoint, payload);
    if (response.status == 200) {
      showSuccessSnackbar(
        `Hours submitted for ${date.toDate().toLocaleDateString()}!`
      );
      refetchSubmissionData();
      if (onSubmit) {
        onSubmit();
      }
    } else {
      showErrorSnackbar("Failed to submit hours.");
    }
    setLoading(false);
  };

  const StyledDayBadge = styled(Badge)(() => ({
    "& .MuiBadge-badge": {
      top: 8,
      right: 5,
    },
  }));

  function CustomDay(props) {
    const { day, outsideCurrentMonth, ...other } = props;
    if (outsideCurrentMonth || dayjs().diff(day, "days") <= 0) {
      return (
        <PickersDay
          day={day}
          outsideCurrentMonth={outsideCurrentMonth}
          {...other}
        />
      );
    }
    const dateKey = day.format("YYYY-MM-DD");
    const count = daysSubmittedSet.has(dateKey) ? 0 : 1;
    return (
      <StyledDayBadge variant="dot" badgeContent={count} color="secondary">
        <PickersDay
          day={day}
          outsideCurrentMonth={outsideCurrentMonth}
          {...other}
        />
      </StyledDayBadge>
    );
  }

  const StyledTotalBadge = styled(Badge)(({ theme }) => ({
    "& .MuiBadge-badge": {
      right: -3,
      top: 0,
      border: `2px solid ${(theme.vars ?? theme).palette.background.paper}`,
      padding: "0 4px",
    },
  }));

  function CustomOpenPickerIcon(props) {
    return (
      <StyledTotalBadge
        overlap="circular"
        badgeContent={daysNotSubmitted}
        color="secondary"
      >
        <CalendarIcon {...props} />
      </StyledTotalBadge>
    );
  }

  useEffect(() => {
    if (!existingSubmissionData) {
      return;
    }
    if (!existingSubmissionData.data.length) {
      setHoursWorked("");
      setJustification("");
      setDisabled(false);
      return;
    }
    const existingSubmission = existingSubmissionData.data[0];
    setHoursWorked(existingSubmission.hours_worked);
    setJustification(existingSubmission.justification);
    const diff = dayjs().diff(dayjs(existingSubmission.submission_dt), "days");
    setDisabled(diff >= 1);
  }, [existingSubmissionData, date]);

  useEffect(() => {
    if (disclosureProps.open) {
      refetchClockEventData();
      refetchPrevSubmissions();
    }
  }, [disclosureProps.open, refetchClockEventData, refetchPrevSubmissions]);

  useEffect(() => {
    if (staticDate) {
      setDate(staticDate);
    }
  }, [staticDate]);

  return (
    <Dialog {...disclosureProps} maxWidth="xs" fullWidth>
      <DialogTitle>Submit Hours</DialogTitle>
      <Box component={"form"} onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={2}>
            {!isStaticDate && (
              <MobileDatePicker
                value={date}
                onChange={setDate}
                minDate={minDate}
                disableFuture
                label="Date"
                slotProps={{
                  textField: { size: "small", fullWidth: true },
                }}
                slots={{
                  openPickerIcon: CustomOpenPickerIcon,
                  day: CustomDay,
                }}
                required
                closeOnSelect
              />
            )}
            {disabled ? (
              <Typography variant="body2" color="error" fontWeight={"bold"}>
                You can only edit your submitted hours for a date within 24
                hours of submission.
              </Typography>
            ) : (
              <Typography variant="body2">
                Based on your clock time, your estimated time worked for{" "}
                {date.toDate().toLocaleDateString()} is{" "}
                <span style={{ fontWeight: "bold" }}>
                  {calculatedHoursWorked} hours
                </span>
              </Typography>
            )}
            <TextField
              value={hoursWorked}
              onChange={(e) => setHoursWorked(e.target.value)}
              type="number"
              label="Hours Worked"
              helperText={`Enter the number of hours you believe you worked on ${date
                .toDate()
                .toLocaleDateString()}`}
              size="small"
              fullWidth
              required
              disabled={disabled}
            />
            {requireReason && (
              <TextField
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                rows={2}
                multiline
                label="Justification"
                helperText={
                  "The time you entered is greater than the time you spent clocked-in. Enter the justification for this difference."
                }
                size="small"
                fullWidth
                required
                disabled={disabled}
              />
            )}
          </Stack>
        </DialogContent>
        {!disabled && (
          <DialogActions>
            <Button type="submit" loading={loading}>
              Submit
            </Button>
          </DialogActions>
        )}
      </Box>
    </Dialog>
  );
}
